import { initializeApp } from 'firebase/app'
import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
} from 'firebase/auth'
import * as rtdbMethods from 'firebase/database'
import { useEffect, useState } from 'react'

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FB_APIKEY,
  authDomain: process.env.REACT_APP_FB_AUTHDOMAIN,
  databaseURL: process.env.REACT_APP_FB_DATABASEURL,
  projectId: process.env.REACT_APP_FB_PROJECTID,
  appId: process.env.REACT_APP_FB_APPID,
}

const truthy = value =>
  typeof value === 'string'
    ? ['1', 'true', 'yes', 'on'].includes(value.toLowerCase())
    : Boolean(value)

const hasRequiredConfig = [
  'apiKey',
  'authDomain',
  'databaseURL',
  'projectId',
  'appId',
].every(key => Boolean(firebaseConfig[key]))

const isFirebaseEnabled =
  hasRequiredConfig && !truthy(process.env.REACT_APP_FB_OFF)

const firebaseApp = isFirebaseEnabled ? initializeApp(firebaseConfig) : null
const firebaseDb = isFirebaseEnabled
  ? rtdbMethods.getDatabase(firebaseApp)
  : null

const createDisabledRealtimeDb = () => {
  // const noop = () => undefined
  const noopAsync = () => Promise.resolve()
  const noopUnsubscribe = () => undefined
  const noopSnapshot = { val: () => null }
  const noopRef = {}
  const returnRef = () => noopRef
  const noopOnDisconnect = () => ({
    set: noopAsync,
    cancel: noopAsync,
    remove: noopAsync,
  })

  return {
    isEnabled: false,
    db: null,
    ref: returnRef,
    child: returnRef,
    query: returnRef,
    orderByChild: returnRef,
    startAt: returnRef,
    onValue: () => noopUnsubscribe,
    onChildAdded: () => noopUnsubscribe,
    onDisconnect: noopOnDisconnect,
    get: () => Promise.resolve(noopSnapshot),
    set: noopAsync,
    update: noopAsync,
  }
}

export const fb = isFirebaseEnabled
  ? {
      isEnabled: true,
      db: firebaseDb,
      ...rtdbMethods,
    }
  : createDisabledRealtimeDb()

export const firebaseSignIn = async getAccessTokenSilently => {
  if (!isFirebaseEnabled) {
    return null
  }
  const auth = getAuth(firebaseApp)
  try {
    const mainToken = await getAccessTokenSilently()
    const url =
      process.env.REACT_APP_API_ENDPOINT +
      process.env.REACT_APP_API_PATH +
      'firebase-token'
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${mainToken}`,
      },
    })
    const { fbToken } = await response.json()
    const { user } = await signInWithCustomToken(auth, fbToken)
    return user
  } catch (err) {
    return null
  }
}

export const useFirebaseAuthStatus = () => {
  const [state, setState] = useState(null)

  useEffect(() => {
    if (!isFirebaseEnabled) {
      return
    }

    const unsubscribe = onAuthStateChanged(getAuth(firebaseApp), user => {
      if (user) {
        setState(user.uid)
      } else {
        setState(null)
      }
    })
    return () => unsubscribe()
  }, [])

  return state
}
